/* eslint-disable max-classes-per-file */
/**
 * An simple decision class.
 *
 * @class Decision
 */
class Decision {
    /**
     * Creates an instance of Decision.
     * @param {Boolean|Function}               condition The condition that must be met.
     * @param {Object|Function|Decision|Array} action    The action to fullfill if somethings met.
     *
     * @memberof Decision
     */
    constructor(condition, action) {
        this.condition = condition;
        this.action = action;

        this.nomatch = {match: false};
    }

    /**
     * Checks sends match result.
     *
     * @param {Object|Array|String|Boolean|Number|Function} value Value to evaluate to.
     *
     * @returns {Object} Match object.
     * @memberof Decision
     */
    match(value) {
        return {
            match: true,
            result: value
        };
    }

    /**
     * Decision evaluation.
     *
     * @param {Array}  data       An array with all steps filled by now.
     * @param {Number} [depth=-1] The actual depth in the tree.
     *
     * @returns {Object|*} The match or the result depending on deepness.
     * @memberof Decision
     */
    evaluate(data, depth = -1) {
        let conditionResult = this.condition;

        if (this.condition instanceof Function) {
            conditionResult = this.condition(data[Number(depth)]);
        }

        if (conditionResult) {
            switch (true) {
                case this.action instanceof Function:
                    return this.match(this.action(data, depth));
                case this.action instanceof DecisionTree:
                    return this.action.evaluate(data, depth);
                case this.action instanceof Decision:
                    return this.action.evaluate(data, depth + 1);
                case this.action instanceof Array:
                    for (let i = this.action.length - 1; i >= 0; i--) {
                        const decision = this.action[Number(i)];

                        if (decision instanceof Decision || decision instanceof DecisionTree) {
                            let result;

                            if (decision instanceof DecisionTree) {
                                result = decision.evaluate(data, depth);
                            } else {
                                result = decision.evaluate(data, depth + 1);
                            }

                            if (result.match) {
                                return (depth === -1) ? result.result : result;
                            }
                        } else {
                            throw new Error('Array of Decision expected');
                        }
                    }

                    return this.nomatch;
                default:
                    return (depth === -1) ? this.match(this.action).result : this.match(this.action);
            }
        }

        return this.nomatch;
    }
}

/**
 * DecissionTree class. Marks most times the start of an decision.
 *
 * @export
 * @class DecisionTree
 * @extends {Decision}
 */
export default class DecisionTree extends Decision {
    /**
     * Creates an instance of DecisionTree.
     *
     * @param {Object|Function|Decision|Array} action The action to fullfill if somethings met.
     * @memberof DecisionTree
     */
    constructor(action) {
        super(true, [new Decision(true, action)]);
    }

    /**
     * Adds an new Branch to the tree.
     *
     * @param {Boolean|Function}               condition The condition that must be met.
     * @param {Object|Function|Decision|Array} action    The action to fullfill if somethings met.
     *
     * @returns {DecisionTree} This tree for chaining.
     * @memberof DecisionTree
     */
    addBranch(condition, action) {
        this.action.push(new Decision(condition, action));

        return this;
    }
}

/**
 * Checks if some data is filled in this step by now.
 *
 * @param {*} data The data for this step.
 *
 * @returns {Boolean} If there is data or not.
 */
export const hasSomeData = data => (typeof data !== 'undefined');